import { FC, forwardRef, ReactNode } from 'react';
import { Box, FormLabel, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './CustomReactDatePicker.module.scss';
import { DateIcon } from '../../components/Icons';
import { formatDateToLocal, parseDate } from '../../utils/dateUtils';

type CalendarPreset = 'default' | 'flightDiscover';

type CustomReactDatePickerProps = {
  required?: boolean;
  disabled?: boolean;
  name?: string;
  inputLabel?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  customClass?: string;
  min?: string;
  max?: string;
  dateFormat?: string;
  calendarPreset?: CalendarPreset;
  flightRange?: {
    rangeRole: 'start' | 'end';
    startIso: string;
    endIso: string;
  };
  inputIconSize?: number;
  inputLeadIcon?: ReactNode;
  reserveHelperSpace?: boolean;
  compactForMobile?: boolean;
  allowClear?: boolean;
  iconPosition?: 'left' | 'right';
};

const startOfLocalDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

type CustomInputProps = {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  iconSize?: number;
  leadIcon?: ReactNode;
  showClear?: boolean;
  onClearClick?: (e: React.MouseEvent) => void;
  iconPosition?: 'left' | 'right';
};

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      value,
      onClick,
      placeholder,
      error,
      disabled,
      iconSize = 24,
      leadIcon,
      showClear,
      onClearClick,
      iconPosition = 'left',
    },
    ref
  ) => {
    const icon = (
      <Box
        className={styles.iconWrapper}
        onClick={onClick}
      >
        {leadIcon ?? (
          <DateIcon
            size={iconSize}
            color="#5E6A81"
          />
        )}
      </Box>
    );

    return (
    <Box className={`${styles.inputWrapper} ${iconPosition === 'right' ? styles.iconRight : ''}`}>
      {iconPosition === 'left' ? icon : null}
      <input
        ref={ref}
        value={value}
        onClick={onClick}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        className={`${styles.customInput} ${error ? styles.error : ''} ${showClear && value ? styles.inputWithClear : ''}`}
      />
      {iconPosition === 'right' ? icon : null}
      {showClear && value ? (
        <IconButton
          type="button"
          size="small"
          className={styles.clearButton}
          aria-label="Clear date"
          disabled={disabled}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onClearClick?.(e);
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
    </Box>
    );
  }
);

const CustomReactDatePicker: FC<CustomReactDatePickerProps> = ({
  required = false,
  disabled = false,
  name = '',
  inputLabel = 'Date',
  placeholder = 'Select date',
  value = '',
  error = false,
  helperText = '',
  onChange,
  customClass,
  min,
  max,
  dateFormat = 'dd/MM/yyyy',
  calendarPreset = 'default',
  flightRange,
  inputIconSize,
  inputLeadIcon,
  reserveHelperSpace = false,
  compactForMobile = false,
  allowClear = false,
  iconPosition = 'left',
}) => {
  const resolvedIconSize = inputIconSize ?? (calendarPreset === 'flightDiscover' ? 20 : 24);
  const dateValue = value ? new Date(value) : null;

  const minDate = min ? new Date(min) : null;
  const maxDate = max ? new Date(max) : null;

  const todayStart = startOfLocalDay(new Date());
  const effectiveMinDate =
    calendarPreset === 'flightDiscover'
      ? (() => {
          if (!minDate) return todayStart;
          const minStart = startOfLocalDay(minDate);
          return minStart.getTime() >= todayStart.getTime() ? minStart : todayStart;
        })()
      : minDate || undefined;

  const rangeStartDate = flightRange?.startIso ? parseDate(flightRange.startIso) : null;
  const rangeEndDate = flightRange?.endIso ? parseDate(flightRange.endIso) : null;
  const useFlightRangeHighlight =
    calendarPreset === 'flightDiscover' && flightRange && rangeStartDate;

  const handleDateChange = (date: Date | null) => {
    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: date ? formatDateToLocal(date) : '',
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
  };

  return (
    <Box
      className={`${styles.customDatePickerContainer} ${customClass ?? ''}`}
      data-invalid={error ? 'true' : undefined}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.customDatePickerLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>

      <DatePicker
        selected={dateValue}
        onChange={handleDateChange}
        disabled={disabled}
        minDate={effectiveMinDate}
        maxDate={maxDate || undefined}
        placeholderText={placeholder}
        dateFormat={dateFormat}
        showPopperArrow={false}
        popperClassName={styles.customPopper}
        calendarClassName={
          calendarPreset === 'flightDiscover' ? styles.flightDiscoverCalendar : styles.customCalendar
        }
        useWeekdaysShort={calendarPreset === 'flightDiscover'}
        {...(useFlightRangeHighlight && flightRange
          ? {
              selectsStart: flightRange.rangeRole === 'start',
              selectsEnd: flightRange.rangeRole === 'end',
              startDate: rangeStartDate as Date,
              endDate: rangeEndDate,
            }
          : {})}
        customInput={
          <CustomInput
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            iconSize={resolvedIconSize}
            leadIcon={inputLeadIcon}
            showClear={allowClear && Boolean(value)}
            onClearClick={() => handleDateChange(null)}
            iconPosition={iconPosition}
          />
        }
      />

      {reserveHelperSpace ? (
        <Box className={styles.helperSlot}>
          {helperText && error ? (
            <Typography
              variant="h11"
              className={styles.errorMessage}
              role="alert"
            >
              {helperText}
            </Typography>
          ) : null}
        </Box>
      ) : (
        helperText &&
        error && (
          <Typography
            variant="h11"
            className={styles.errorMessage}
          >
            {helperText}
          </Typography>
        )
      )}
    </Box>
  );
};

export default CustomReactDatePicker;
